import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'

import {
    ArrowDown,
    ArrowUp,
    DownloadSimple,
} from '@phosphor-icons/react'

import ReactApexChart from 'react-apexcharts'

import merchantApi from '../../services/merchantApi'

import './SalesReportPage.scss'
import Header from '../../components/Header/Header'
import NoImage from '../../assets/images/no-image.png'
import DateRange from '../../components/DateRange/DateRange'
import { X } from 'lucide-react'

const moduleCode = 'report'

const DEFAULT_IMAGE = NoImage

const getPayload = (response) => {
    console.log('getPayload received:', response)
    // Check if response has a top-level data key (success/message/data)
    if (response && 'data' in response && response.data) {
        console.log('getPayload returning response.data')
        return response.data
    }
    // Fallback to whole response if nothing else
    console.log('getPayload returning response or {}')
    return response || {}
}

const asNumber = (value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback
    const cleaned = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value
    const number = Number(cleaned)
    return Number.isFinite(number) ? number : fallback
}

const pick = (source, keys, fallback = undefined) => {
    for (const key of keys) {
        if (source && source[key] !== undefined && source[key] !== null) {
            return source[key]
        }
    }

    return fallback
}

const formatMoney = (value) => {
    const amount = asNumber(value)

    return `RM${amount.toLocaleString('en-MY', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`
}

const formatNumber = (value) => asNumber(value).toLocaleString('en-MY')

const formatPercent = (value) => {
    if (value === null || value === undefined || value === '') return null
    const number = asNumber(value)
    return `${Math.abs(number).toFixed(1)}%`
}

const formatDateLabel = (value) => {
    if (!value) return '-'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)

    return date.toLocaleDateString('en-MY', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    })
}

const normalizeSalesReport = (response) => {
    const payload = getPayload(response)
    const summary = payload.summary || payload.overview || payload.totals || payload

    const totalRevenue = pick(summary, ['total_revenue', 'revenue', 'net_revenue', 'total_sales'], 0)
    const totalOrders = pick(summary, ['total_orders', 'orders', 'order_count'], 0)
    const avgOrderValue = pick(summary, ['avg_order_value', 'average_order_value', 'aov'], 0)

    const revenueTrend = pick(summary, ['revenue_trend', 'revenue_growth', 'revenue_change_percentage'], null)
    const ordersTrend = pick(summary, ['orders_trend', 'orders_growth', 'orders_change'], null)
    const avgTrend = pick(summary, ['avg_order_value_trend', 'aov_growth', 'aov_change_percentage'], null)

    const breakdownRows = payload.breakdown || payload.daily_breakdown || payload.daily_sales || payload.sales_by_day || []
    const breakdown = Array.isArray(breakdownRows)
        ? breakdownRows.map((row) => {
            const orders = pick(row, ['orders', 'total_orders', 'order_count'], 0)
            const revenue = pick(row, ['revenue', 'net_revenue', 'total_revenue', 'sales'], 0)
            const avg = pick(row, ['avg_order_value', 'average_order_value', 'aov'], asNumber(orders) ? asNumber(revenue) / asNumber(orders) : 0)

            return {
                date: formatDateLabel(pick(row, ['date_label', 'label', 'date', 'day'], '-')),
                orders,
                revenue,
                avg,
            }
        })
        : []

    const chartRows = payload.revenue_trend || payload.revenue_chart || payload.chart || payload.daily_revenue || breakdownRows || []
    const chart = Array.isArray(chartRows)
        ? chartRows.map((row) => ({
            label: formatDateLabel(pick(row, ['date_label', 'label', 'date', 'day'], '-')),
            revenue: asNumber(pick(row, ['revenue', 'net_revenue', 'total_revenue', 'sales', 'amount'], 0)),
        }))
        : []

    const itemRows = payload.top_items || payload.top_selling_items || payload.top_selling || payload.top_products || []
    const topItems = Array.isArray(itemRows)
        ? itemRows.map((item, index) => ({
            rank: index + 1,
            name: pick(item, ['name', 'product_name', 'title'], 'Unnamed item'),
            category: pick(item, ['category', 'category_name'], 'Item'),
            price: pick(item, ['price', 'normal_price', 'selling_price'], 0),
            revenue: pick(item, ['revenue', 'total_revenue', 'sales', 'amount', 'total_sales'], 0),
            sold: pick(item, ['sold', 'quantity_sold', 'qty', 'quantity'], 0),
            image: pick(item, ['image', 'image_url', 'thumbnail_image', 'photo'], DEFAULT_IMAGE) || DEFAULT_IMAGE,
        }))
        : []

    return {
        summary: {
            totalRevenue,
            totalOrders,
            avgOrderValue,
            revenueTrend,
            ordersTrend,
            avgTrend,
        },
        breakdown,
        chart,
        topItems,
        outletName: pick(payload, ['outlet_name', 'merchant_name', 'store_name'], 'Cuscapi Merchant'),
    }
}

const getRangeParams = (range) => {
    if (range === 'week') return { period: 'week' }
    if (range === 'month') return { period: 'month' }
    if (range === 'custom') return { period: 'custom' }
    return { period: 'today' }
}

function CustomDateRangeModal({ open, fromDate, toDate, onFromChange, onToChange, onClose, onApply }) {
    if (!open) return null

    return createPortal(
        <div className="sales-range-modal-overlay" onClick={onClose}>
            <div className="sales-range-modal" onClick={(event) => event.stopPropagation()}>
                <div className="sales-range-modal__header">
                    <h3>Pick your date range</h3>
                    <button type="button" className="sales-range-modal__close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <div className="sales-range-modal__body">
                    <DateRange
                        startDate={fromDate}
                        endDate={toDate}
                        onStartDateChange={onFromChange}
                        onEndDateChange={onToChange}
                    />
                </div>

                <div className="sales-range-modal__footer">
                    <button type="button" className="sales-range-modal__btn sales-range-modal__btn--ghost" onClick={onClose}>
                        Cancel
                    </button>
                    <button type="button" className="sales-range-modal__btn sales-range-modal__btn--primary" onClick={onApply} disabled={!fromDate || !toDate}>
                        Apply
                    </button>
                </div>
            </div>
        </div>,
        document.body
    )
}

export default function SalesReportPage() {
    const navigate = useNavigate()
    const [range, setRange] = useState('today')
    const [report, setReport] = useState(() => normalizeSalesReport({}))
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [userInfo, setUserInfo] = useState(null)
    const [startDate, setStartDate] = useState(null)
    const [endDate, setEndDate] = useState(null)
    const [draftStartDate, setDraftStartDate] = useState(null)
    const [draftEndDate, setDraftEndDate] = useState(null)
    const [showCustomRangeModal, setShowCustomRangeModal] = useState(false)
    const [selectedOutletId, setSelectedOutletId] = useState(null)

    const [exportable, setExportable] = useState(true)

    useEffect(() => {
        let active = true

        const loadReport = async () => {
            setLoading(true)
            setError('')

            let payload = {
                ...getRangeParams(range),
                outlet_id: selectedOutletId,
            }
            if (range === 'custom') {
                payload.start_date = startDate // YYYY-MM-DD format
                payload.end_date = endDate
            }

            try {
                const response = await merchantApi.reports.sales(payload)
                console.log('Full API response:', response)
                if (active) {
                    const normalized = normalizeSalesReport(response)
                    console.log('Normalized report:', normalized)
                    setReport(normalized)
                }
            } catch (err) {
                if (active) {
                    setError(err?.message || 'Unable to load sales report.')
                    setReport(normalizeSalesReport({}))
                }
            } finally {
                if (active) {
                    setLoading(false)
                }
            }
        }

        if(selectedOutletId){
           loadReport() 
        }

        return () => {
            active = false
        }
    }, [range, startDate, endDate, selectedOutletId])

    useEffect(() => {
        loadUserdData()
    }, [])


    const loadUserdData = async () => {
        try {
            const response = await merchantApi.auth.me();
            if (!response?.data?.permissions?.includes(`view-${moduleCode}`)) {
                navigate('/unauthorized')
                return
            }
            setExportable(response?.data?.permissions?.includes(`export-${moduleCode}`))
            setUserInfo(response?.data || null)
            setSelectedOutletId(response?.data?.default_outlet?.id || null)
        } catch (error) {
            console.log("Error fetching data:", error)
        }
    }

    // Prepare ApexCharts data
    const chartOptions = {
        chart: {
            type: 'area',
            toolbar: {
                show: false
            },
            zoom: {
                enabled: false
            }
        },
        colors: ['#15b6cd'],
        fill: {
            type: 'gradient',
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.45,
                opacityTo: 0.05,
                stops: [0, 90, 100]
            }
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'smooth',
            width: 3
        },
        xaxis: {
            categories: report.chart.map((item) => item.label),
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            }
        },
        yaxis: {
            labels: {
                formatter: function (value) {
                    if (value >= 1000) return `${Math.round(value / 1000)}K`
                    return Math.round(value).toString()
                }
            }
        },
        grid: {
            borderColor: '#e2e8f0',
            strokeDashArray: 4
        },
        legend: {
            position: 'bottom',
            horizontalAlign: 'center'
        },
        tooltip: {
            y: {
                formatter: function (value) {
                    return `RM ${value.toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                }
            }
        }
    }

    const chartSeries = [
        {
            name: 'Revenue',
            data: report.chart.map((item) => asNumber(item.revenue))
        }
    ]

    const handleExportCsv = async () => {
        try {
            const response = await merchantApi.reports.export({ period: range })
            const byteCharacters = atob(response?.data?.base64);
            const byteNumbers = new Array(byteCharacters.length)
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i)
            }
            const byteArray = new Uint8Array(byteNumbers)
            const blob = new Blob([byteArray], { type: response?.data?.mime_type || 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = response?.data?.file_name || 'customers.csv'
            document.body.appendChild(a)
            a.click()
            document.body.removeChild(a)
        } catch (error) {
            console.log("Error exporting CSV:", error)
            setError(error?.message || 'Unable to export sales report.')
        }
    }

    const renderTrend = (value, fallbackText) => {
        const trend = formatPercent(value)
        const positive = asNumber(value) >= 0

        return (
            <div className={`trend ${positive ? 'positive' : 'negative'}`}>
                {positive ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
                {trend || fallbackText}
            </div>
        )
    }

    const handleRangeSelect = (nextRange) => {
        if (nextRange === 'custom') {
            setDraftStartDate(startDate)
            setDraftEndDate(endDate)
            setShowCustomRangeModal(true)
            return
        }

        setShowCustomRangeModal(false)
        setRange(nextRange)
    }

    const handleApplyCustomRange = () => {
        if (!draftStartDate || !draftEndDate) return

        setStartDate(draftStartDate)
        setEndDate(draftEndDate)
        setRange('custom')
        setShowCustomRangeModal(false)
    }

    return (
        <div className="sales-report-page">
            <Header
                items={[
                    { label: userInfo?.merchant?.business_name || 'Merchant', to: '/' },
                    { label: 'Sales report', current: true },
                ]}
            />
            <div className="page-header">
                <div>
                    <h1>
                        Sales report
                    </h1>

                    <p>
                        Track revenue, orders, average order value, and top selling items.
                    </p>
                </div>

                <div className="report-actions">
                    <div className="report-tabs">
                        <button
                            className={range === 'today' ? 'active' : ''}
                            onClick={() => handleRangeSelect('today')}
                            type="button"
                        >
                            Today
                        </button>

                        <button
                            className={range === 'week' ? 'active' : ''}
                            onClick={() => handleRangeSelect('week')}
                            type="button"
                        >
                            Week
                        </button>

                        <button
                            className={range === 'month' ? 'active' : ''}
                            onClick={() => handleRangeSelect('month')}
                            type="button"
                        >
                            Month
                        </button>
                        <button
                            className={range === 'custom' || showCustomRangeModal ? 'active' : ''}
                            onClick={() => handleRangeSelect('custom')}
                            type="button"
                        >
                            Custom dates
                        </button>
                    </div>
                    {
                        exportable &&
                        <button
                            className="btn-export"
                            onClick={handleExportCsv}
                            type="button"
                            disabled={!report.breakdown.length}
                        >
                            <DownloadSimple size={18} />
                            Export CSV
                        </button>
                    }
                </div>
            </div>

            {error ? (
                <div className="report-alert">
                    {error}
                </div>
            ) : null}

            {loading ? (
                <div className="report-loading">
                    Loading sales report...
                </div>
            ) : null}

            <div className="stats-grid">
                <div className="stat-card">
                    <span>
                        TOTAL REVENUE
                    </span>

                    <h2>
                        {formatMoney(report.summary.totalRevenue)}
                    </h2>

                    {renderTrend(report.summary.revenueTrend, '0.0%')}
                </div>

                <div className="stat-card">
                    <span>
                        TOTAL ORDERS
                    </span>

                    <h2>{formatNumber(report.summary.totalOrders)}</h2>

                    {renderTrend(report.summary.ordersTrend, '0 orders')}
                </div>

                <div className="stat-card">
                    <span>
                        AVG ORDER VALUE
                    </span>

                    <h2>
                        {formatMoney(report.summary.avgOrderValue)}
                    </h2>

                    {renderTrend(report.summary.avgTrend, '0.0%')}
                </div>
            </div>

            <div className="report-grid">
                <div className="report-card">
                    <div className="report-card__header">
                        Revenue trend
                    </div>

                    <div className="apex-charts">
                        <ReactApexChart
                            options={chartOptions}
                            series={chartSeries}
                            type="area"
                            height={260}
                        />
                    </div>
                </div>

                <div className="report-card">
                    <div className="report-card__header">
                        Daily breakdown
                    </div>

                    <div className="breakdown-table">
                        <div className="breakdown-head">
                            <span>
                                DATE
                            </span>
                            <span>
                                ORDERS
                            </span>
                            <span>
                                NET REVENUE
                            </span>
                            <span>
                                AVG ORDER VALUE
                            </span>
                        </div>

                        {report.breakdown.length ? report.breakdown.map((item, index) => (
                            <div
                                className="breakdown-row"
                                key={`${item.date}-${index}`}
                            >
                                <span>
                                    {item.date}
                                </span>

                                <span>
                                    {formatNumber(item.orders)}
                                </span>

                                <span>
                                    {formatMoney(item.revenue)}
                                </span>

                                <span>
                                    {formatMoney(item.avg)}
                                </span>
                            </div>
                        )) : (
                            <div className="breakdown-empty">
                                No sales data found for this period.
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className="report-card">
                <div className="report-card__header">
                    Top selling items
                </div>

                <div className="top-items-grid">
                    {report.topItems.length ? report.topItems.map((item, index) => (
                        <div
                            className="top-item"
                            key={`${item.name}-${index}`}
                        >
                            <div className="top-item__left">
                                <div className="top-item__rank">
                                    {item.rank}
                                </div>

                                <img
                                    src={item.image}
                                    alt={item.name}
                                />

                                <div className="top-item__content">
                                    <h4>
                                        {item.name}
                                    </h4>

                                    <p>
                                        {item.category}{' '}
                                        {/* ・{' '} */}
                                        {/* {formatMoney(item.price)} */}
                                    </p>
                                </div>
                            </div>

                            <div className="top-item__right">
                                <h5>
                                    {formatMoney(item.revenue)}
                                </h5>

                                <span>
                                    {formatNumber(item.sold)} sold
                                </span>
                            </div>
                        </div>
                    )) : (
                        <div className="breakdown-empty">
                            No top selling items found for this period.
                        </div>
                    )}
                </div>
            </div>

            <CustomDateRangeModal
                open={showCustomRangeModal}
                fromDate={draftStartDate}
                toDate={draftEndDate}
                onFromChange={setDraftStartDate}
                onToChange={setDraftEndDate}
                onClose={() => setShowCustomRangeModal(false)}
                onApply={handleApplyCustomRange}
            />
        </div>
    )
}
